import * as React from "react";
import {lazyInject} from "@athena-portal/core";
import {Types} from "../Types";
import {IFerryConvenienceApiService} from "../services/IFerryConvenienceApiService";

interface IProps {
    initialMake?: string;
    initialModel?: string;
    onMakeSelected: (make: string) => void;
    onModelSelected: (model: string) => void;
    onMakeEdited: (make: string) => void;
    onModelEdited: (model: string) => void;
    disableMakeInput: boolean;
}

interface IState {
    vehicleMakeList: string[];
    vehicleModelList: string[];
    fullVehicleManufacturer: string;
    fullVehicleModel: string;
    manufacturerValid: string;
    modelValid: string;
    modelFound: string;
    makeSelectedFromLookup: boolean;
    modelSelectedFromLookup: boolean;
    makeLoading: boolean;
    modelLoading: boolean;
    modelReadOnly: boolean;
}

export default class VehicleMakeModelSelector extends React.Component<IProps, IState> {
    @lazyInject(Types.FerryConvenienceApiService)
    private readonly ferryConvenienceApiService: IFerryConvenienceApiService;

    private readonly IS_VALID = "is-valid";
    private readonly IS_INVALID = "is-invalid";
    private debounceTimeout: NodeJS.Timeout | null = null;
    private readonly DEBOUNCE_DELAY = 500; // milliseconds

    constructor(props: IProps) {
        super(props);

        const hasInitialModel = !!props.initialModel && props.initialModel.trim() !== '';

        this.state = {
            vehicleMakeList: [],
            vehicleModelList: [],
            fullVehicleManufacturer: props.initialMake ?? '',
            fullVehicleModel: props.initialModel ?? '',
            manufacturerValid: '',
            modelValid: hasInitialModel ? this.IS_VALID : '',
            modelFound: '',
            makeLoading: false,
            modelLoading: false,
            modelReadOnly: props.initialMake == null || props.initialMake === "",
            makeSelectedFromLookup: false,
            modelSelectedFromLookup: false,
        };

        this.handleMakeChange = this.handleMakeChange.bind(this);
        this.onSelectVehicleMake = this.onSelectVehicleMake.bind(this);
    }

    // Update states after VRN lookup returns new make and model
    public componentDidUpdate(prevProps: IProps) {

        if (this.props.initialModel !== prevProps.initialModel &&
            this.props.initialModel !== this.state.fullVehicleModel) {

            const newModel: string = this.props.initialModel ?? '';

            this.setState({
                fullVehicleModel: newModel,
                modelValid: newModel ? this.IS_VALID : '',
                vehicleModelList: [],
                modelFound: ''
            });
        }

        if (this.props.initialMake !== prevProps.initialMake &&
            this.props.initialMake !== this.state.fullVehicleManufacturer) {

            const newMake: string = this.props.initialMake ?? '';

            this.setState({
                fullVehicleManufacturer: newMake,
                manufacturerValid: newMake ? this.IS_VALID : '',
                modelReadOnly: false
            });
        }
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>
                <div className="mb-3">
                    <label htmlFor="make" className="form-label">Make</label>
                    <input
                        type="text"
                        className={`form-control w-100 ${this.state.manufacturerValid}`}
                        id="make"
                        aria-describedby="makeDescription"
                        value={this.state.fullVehicleManufacturer}
                        onChange={this.handleMakeChange}
                        readOnly={this.props.disableMakeInput}
                        placeholder={this.props.disableMakeInput ? "" : "Start typing the make"}
                        required={true}
                        autoComplete="off"
                    />
                    <div id="modelDescription"
                        aria-label={this.ariaSuggestionsAvailableString(this.state.vehicleMakeList)}
                    />
                    {this.state.makeLoading && (
                        <div className="text-muted mt-1">Loading suggestions...</div>
                    )}
                    {this.renderFoundVehicleMakes()}
                    {this.state.manufacturerValid === this.IS_INVALID &&
                        <div className="invalid-feedback d-block">Warning: Make not found.</div>
                    }
                </div>
                <div className="mb-3">
                    <label htmlFor="model" className="form-label">Model</label>
                    <div className="addressLookup w-100">
                        <input type="text"
                               className={`form-control w-100 ${this.state.modelValid} ${this.state.modelFound}`}
                               id="model"
                               aria-describedby="modelDescription"
                               value={this.state.fullVehicleModel}
                               onChange={this.handleModelChange}
                               onClick={this.loadModelsOnFirstClick}
                               readOnly={this.state.modelReadOnly}
                               placeholder={this.state.modelReadOnly ? "" : "Start typing the model"}
                               required={true}
                               autoComplete="off"
                        />
                        <div id="modelDescription"
                             aria-label={this.ariaSuggestionsAvailableString(this.state.vehicleModelList)}/>
                        {this.state.modelLoading && (
                            <div className="text-muted mt-1">Loading suggestions...</div>
                        )}
                        {this.renderFoundVehicleModels()}
                        {this.state.modelFound === this.IS_INVALID &&
                            <div className="invalid-feedback d-block">Warning: Model not found.</div>}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private readonly handleModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        this.setState({
            fullVehicleModel: inputValue,
            modelSelectedFromLookup: false,
            modelLoading: inputValue.trim().length > 0
        });

        this.props.onModelEdited(inputValue);

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        if (!inputValue.trim()) {
            this.setState({
                vehicleModelList: [],
                modelFound: '',
                modelLoading: false
            });
            return;
        }

        this.debounceTimeout = setTimeout(() => {
            this.fetchVehicleModels(this.state.fullVehicleManufacturer, inputValue);
        }, this.DEBOUNCE_DELAY);
    };

    private readonly loadModelsOnFirstClick = (event: React.MouseEvent<HTMLInputElement>): void => {
        if (!this.state.fullVehicleModel && this.state.fullVehicleManufacturer) {
            this.setState({ modelLoading: true }, () => {
                this.fetchVehicleModels(this.state.fullVehicleManufacturer, "");
            });
        }
    };

    private readonly onSelectVehicleModel = (event: any) => {
        const selectedModel = event.currentTarget.textContent;
        if (selectedModel) {
            this.setState({
                fullVehicleModel: selectedModel,
                vehicleModelList: [],
                modelValid: this.IS_VALID,
                modelSelectedFromLookup: true
            });

            this.props.onModelSelected(selectedModel);
        }
    };

    private ariaSuggestionsAvailableString(array: any[]): string {
        const defaultSTR: string = "Suggestion size ";
        if (array && array.length > 0) {
            return defaultSTR + array.length;
        } else {
            return '';
        }
    }

    private handleMakeChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const inputValue: string = event.target.value;

        this.setState({
            fullVehicleManufacturer: inputValue,
            makeSelectedFromLookup: false,
            makeLoading: inputValue.trim().length > 0,
            modelReadOnly: true
        });

        this.props.onMakeEdited(inputValue);

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        if (!inputValue.trim()) {
            this.setState({
                vehicleMakeList: [],
                makeLoading: false
            });
            return;
        }

        this.debounceTimeout = setTimeout(() => {
            this.fetchVehicleMakes(inputValue);
        }, this.DEBOUNCE_DELAY);
    }

    private fetchVehicleMakes(partialMake: string) {
        if (partialMake) {
            this.ferryConvenienceApiService.getVehicleManufacturerDetails(partialMake).subscribe({
                next: (output: string[]) => {
                    this.setState({
                        vehicleMakeList: output,
                        manufacturerValid: output.length > 0 ? this.IS_VALID : this.IS_INVALID,
                        makeLoading: false
                    })
                },
                error: () => {
                    this.setState({
                        vehicleMakeList: [],
                        manufacturerValid: this.IS_INVALID,
                        makeLoading: false
                    })
                }
            });
        }
    }

    private fetchVehicleModels(make: string, model: string) {
        if (!make) {
            return;
        }

        this.ferryConvenienceApiService.getVehicleShortenedModelDetails(make, model)
            .subscribe({
                next: (output: string[]) => {
                    this.setState({
                        vehicleModelList: output,
                        modelFound: output.length > 0 ? this.IS_VALID : this.IS_INVALID,
                        modelLoading: false,
                        modelReadOnly: false
                    });
                },
                error: () => {
                    this.setState({
                        vehicleModelList: [],
                        modelFound: this.IS_INVALID,
                        modelLoading: false,
                        modelReadOnly: false
                    });
                }
            });
    }

    private onSelectVehicleMake(event: any): void {
        const selectedMake: string | null = event.currentTarget.textContent;

        if (selectedMake) {
            this.setState({
                fullVehicleManufacturer: selectedMake,
                vehicleMakeList: [],
                manufacturerValid: this.IS_VALID,
                makeSelectedFromLookup: true,
                modelReadOnly: false
            }, () => {
                if (this.props.onMakeSelected) {
                    this.props.onMakeSelected(selectedMake);
                }
            });
        }
    }

    private renderFoundVehicleMakes(): React.ReactNode {
        const vehicleMakes: React.ReactNode[] = [];

        if (this.state.vehicleMakeList && this.state.vehicleMakeList.length > 0) {
            this.state.vehicleMakeList.forEach((vehicleMake, index) => {
                vehicleMakes.push(
                    <li
                        key={vehicleMake}
                        data-id={index}
                    >
                        <button type="button" onClick={this.onSelectVehicleMake}>
                            {vehicleMake}
                        </button>
                    </li>
                )
            });

            return (
                <div className="vehicleLookupWrapper">
                    <ul className="vehiclesFound" role="listbox" // NOSONAR - Suggestion is to use select while it is not applicable for autocomplete box as you can't type in a regular select
                    >
                        {vehicleMakes}
                    </ul>
                </div>
            );
        }

        return <></>
    }

    private renderFoundVehicleModels(): React.ReactNode {
        const vehicleModels: React.ReactNode[] = [];

        if (this.state.vehicleModelList && this.state.vehicleModelList.length > 0) {
            this.state.vehicleModelList.forEach((vehicleModel, index) => {
                vehicleModels.push(
                    <li
                        key={vehicleModel}
                        data-id={index}
                    >
                        <button type="button" onClick={this.onSelectVehicleModel}>
                            {vehicleModel}
                        </button>
                    </li>
                );
            });

            return (
                <div className="vehicleLookupWrapper">
                    <ul className="vehiclesFound" role="listbox" // NOSONAR - Suggestion is to use select while it is not applicable for autocomplete box as you can't type in a regular select
                    >
                        {vehicleModels}
                    </ul>
                </div>
            );
        }

        return null;
    }
}
